import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  pending:           { label:'Pending Review',    color:'#92400e', bg:'#fef3c7', border:'#fde68a', icon:'⏳' },
  approved:          { label:'Approved',          color:'#065f46', bg:'#d1fae5', border:'#6ee7b7', icon:'✅' },
  rejected:          { label:'Rejected',          color:'#991b1b', bg:'#fee2e2', border:'#fca5a5', icon:'❌' },
  changes_requested: { label:'Changes Requested', color:'#1e40af', bg:'#dbeafe', border:'#93c5fd', icon:'✏️' },
};

// Shared responsibility picker used by both MyProfile and ReviewModal
export function ResponsibilityPicker({ responsibilities, setResponsibilities, templates, disabled }) {
  const grouped = templates.reduce((a,t) => ({ ...a, [t.category]: [...(a[t.category]||[]), t] }), {});

  const addTemplate = (text) => {
    if (!responsibilities.includes(text))
      setResponsibilities(r => [...r.filter(x => x.trim()), text, '']);
  };
  const addCustom    = () => setResponsibilities(r => [...r, '']);
  const removeResp   = i  => setResponsibilities(r => r.filter((_,idx) => idx !== i));
  const updateResp   = useCallback((i, v) => setResponsibilities(r => r.map((x,idx) => idx===i ? v : x)), [setResponsibilities]);

  return (
    <div>
      {/* Template picker */}
      {!disabled && (
        <div style={{background:'var(--slate-50)',borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:600,color:'var(--slate-600)',marginBottom:10}}>Quick Select from Templates:</div>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} style={{marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--slate-400)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.5px'}}>{cat}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {items.map(t => {
                  const selected = responsibilities.includes(t.text);
                  return (
                    <button key={t.id} onClick={() => selected ? setResponsibilities(r=>r.filter(x=>x!==t.text)) : addTemplate(t.text)}
                      style={{padding:'5px 11px',borderRadius:20,border:`1px solid ${selected?'var(--indigo)':'var(--slate-200)'}`,background:selected?'var(--indigo-pale)':'#fff',color:selected?'var(--indigo)':'var(--slate-600)',fontSize:12,cursor:'pointer',fontWeight:selected?600:400,transition:'all .15s'}}>
                      {selected ? '✓ ' : ''}{t.text}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Entries */}
      {responsibilities.map((r, i) => (
        <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
          <span style={{width:24,height:24,background:'var(--indigo)',color:'#fff',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
          <input value={r} disabled={disabled} onChange={e => updateResp(i, e.target.value)}
            placeholder="Describe your responsibility…"
            style={{flex:1,padding:'8px 12px',border:'1px solid var(--slate-300)',borderRadius:7,fontSize:13,background:disabled?'var(--slate-50)':'#fff'}} />
          {!disabled && responsibilities.length > 1 && (
            <button onClick={() => removeResp(i)} style={{padding:'6px 10px',border:'1px solid var(--slate-200)',borderRadius:6,background:'#fff',color:'var(--red)',cursor:'pointer'}}>✕</button>
          )}
        </div>
      ))}
      {!disabled && (
        <button onClick={addCustom} style={{marginTop:4,padding:'6px 14px',borderRadius:7,border:'1px solid var(--slate-300)',background:'#fff',fontSize:12,cursor:'pointer',color:'var(--slate-600)'}}>+ Add Custom</button>
      )}
    </div>
  );
}

export default function MyProfile() {
  const { user } = useAuth();
  const empId = user?.employee?.id;

  const [emp, setEmp]               = useState(null);
  const [submission, setSubmission] = useState(null);
  const [templates, setTemplates]   = useState([]);
  const [tab, setTab]               = useState('status');
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [form, setForm] = useState({
    phone:'', address:'', emergency_contact_name:'', emergency_contact_phone:'',
    university:'', faculty:'', degree:'', internship_start:'', internship_end:'',
    project_title:'', letter_type:'Internship', time_type:'Full Time', signatory_name:'Mr. H. M. R. U. K. Bandara',
    signatory_title:'Chief Executive Officer (CEO)', requested_by:'',
  });
  const [responsibilities, setResponsibilities] = useState(['']);

  const load = () => {
    if (!empId) return;
    api.get(`/employees/${empId}`).then(r => setEmp(r.data.data));
    api.get('/my/submission').then(r => {
      const s = r.data.data;
      setSubmission(s);
      if (s) {
        setForm({
          phone:s.phone||'', address:s.address||'',
          emergency_contact_name:s.emergency_contact_name||'',
          emergency_contact_phone:s.emergency_contact_phone||'',
          university:s.university||'', faculty:s.faculty||'',
          degree:s.degree||'', internship_start:s.internship_start?.slice(0,10)||'',
          internship_end:s.internship_end?.slice(0,10)||'',
          project_title:s.project_title||'', letter_type:s.letter_type||'Internship',
          signatory_name:s.signatory_name||'Mr. H. M. R. U. K. Bandara',
          signatory_title:s.signatory_title||'Chief Executive Officer (CEO)',
          requested_by:s.requested_by||'',
        });
        if (s.responsibilities?.length > 0)
          setResponsibilities(s.responsibilities.map(r => r.responsibility));
      }
    });
    api.get('/responsibility-templates').then(r => setTemplates(r.data.data));
  };

  useEffect(() => { load(); }, [empId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePreview = async () => {
    if (!form.internship_start || !form.internship_end) {
      alert('Please fill in the Internship Start and End dates before previewing.');
      return;
    }
    setPreviewing(true);
    try {
      const employeeData = {
        first_name: emp?.first_name, last_name: emp?.last_name,
        gender: emp?.gender, university: form.university, faculty: form.faculty,
        internship_start: form.internship_start, internship_end: form.internship_end,
      };
      const res = await api.post('/letters/preview', {
        employeeData,
        signatoryName: form.signatory_name || 'Mr. H. M. R. U. K. Bandara',
        signatoryTitle: form.signatory_title || 'Chief Executive Officer (CEO)',
        requestedBy: form.requested_by,
        timeType: form.time_type,
        startDate: form.internship_start || null,
        endDate: form.internship_end || null,
        university: form.university || null,
        faculty: form.faculty || null,
        letterStyle: form.letter_type === 'Internship' ? 'internship' : form.letter_type === 'Associate' ? 'associate' : form.letter_type === 'Appointment' ? 'appointment' : 'permanent',
        responsibilities: responsibilities.filter(r => r.trim()),
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { alert('Preview failed'); }
    setPreviewing(false);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.post('/my/submission', { ...form, responsibilities: responsibilities.filter(r=>r.trim()) });
      setSaved(true);
      load();
      setTab('status');
    } catch(e) { alert(e.response?.data?.message || 'Failed to submit'); }
    setSaving(false);
  };

  const canEdit = !submission || submission.status === 'changes_requested' || submission.status === 'rejected';
  const status  = submission ? STATUS_CONFIG[submission.status] : null;

  if (!emp) return <div style={{padding:40,textAlign:'center',color:'var(--slate-400)'}}>Loading…</div>;

  const initials = `${emp.first_name?.[0]||''}${emp.last_name?.[0]||''}`.toUpperCase();
  const current  = emp.history?.find(h => h.is_current);

  return (
    <div style={{ maxWidth:780, margin:'0 auto' }}>
      {/* Header */}
      <div className="profile-header" style={{marginBottom:24}}>
        <div className="profile-avatar">{initials}</div>
        <div style={{flex:1}}>
          <div className="profile-name">{emp.first_name} {emp.last_name}</div>
          <div className="profile-role">{current?.designation_title||'—'} · {current?.department||''}</div>
          <div className="profile-meta"><span>{emp.employee_id}</span><span>{emp.email}</span></div>
        </div>
        {status && (
          <div style={{background:status.bg,border:`1px solid ${status.border}`,borderRadius:12,padding:'12px 18px',textAlign:'center',flexShrink:0}}>
            <div style={{fontSize:22}}>{status.icon}</div>
            <div style={{fontSize:12,fontWeight:700,color:status.color,marginTop:4}}>{status.label}</div>
          </div>
        )}
      </div>

      {saved && <div className="alert alert-success">✅ Submitted! Waiting for admin review.</div>}

      {submission?.admin_notes && (submission.status==='changes_requested'||submission.status==='rejected') && (
        <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'14px 18px',marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1e40af',marginBottom:4}}>📋 Admin Notes</div>
          <div style={{fontSize:13,color:'#1e3a8a'}}>{submission.admin_notes}</div>
        </div>
      )}

      <div className="tabs">
        {[['status','My Status'],['form','Details & Request'],['profile','My Profile']].map(([k,l]) => (
          <div key={k} className={`tab ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</div>
        ))}
      </div>

      {/* STATUS TAB */}
      {tab==='status' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Submission Status</span></div>
          <div className="card-body">
            {!submission ? (
              <div className="empty-state">
                <svg fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" style={{width:48,height:48,color:'var(--slate-300)',margin:'0 auto 12px',display:'block'}}><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <p>No submission yet.</p>
                <button className="btn btn-primary" style={{marginTop:12}} onClick={()=>setTab('form')}>Fill My Details</button>
              </div>
            ) : (
              <div>
                <div style={{background:status.bg,border:`1px solid ${status.border}`,borderRadius:10,padding:'16px 20px',marginBottom:20,display:'flex',alignItems:'center',gap:14}}>
                  <span style={{fontSize:28}}>{status.icon}</span>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:status.color}}>{status.label}</div>
                    <div style={{fontSize:12,color:status.color,opacity:.8,marginTop:2}}>
                      Submitted: {new Date(submission.submitted_at).toLocaleString('en-GB')}
                      {submission.reviewed_at && ` · Reviewed: ${new Date(submission.reviewed_at).toLocaleString('en-GB')}`}
                    </div>
                  </div>
                  {canEdit && <button className="btn btn-outline btn-sm" style={{marginLeft:'auto'}} onClick={()=>setTab('form')}>Edit & Resubmit</button>}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
                  {[['Letter Type',submission.letter_type],['University',submission.university],['Faculty',submission.faculty],['Degree',submission.degree],['From',submission.internship_start?new Date(submission.internship_start).toLocaleDateString('en-GB'):'—'],['To',submission.internship_end?new Date(submission.internship_end).toLocaleDateString('en-GB'):'—'],['Project',submission.project_title],['Signatory',submission.signatory_name]].filter(([,v])=>v&&v!=='—').map(([k,v])=>(
                    <div key={k} style={{background:'var(--slate-50)',borderRadius:8,padding:'10px 14px'}}>
                      <div style={{fontSize:11,fontWeight:600,color:'var(--slate-500)'}}>{k}</div>
                      <div style={{fontSize:13,color:'var(--navy)',marginTop:2}}>{v}</div>
                    </div>
                  ))}
                </div>
                {submission.responsibilities?.length>0 && (
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:8}}>Responsibilities</div>
                    {submission.responsibilities.map((r,i)=>(
                      <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',marginBottom:6}}>
                        <span style={{width:22,height:22,background:'var(--indigo)',color:'#fff',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
                        <span style={{fontSize:13,color:'var(--slate-700)',paddingTop:2}}>{r.responsibility}</span>
                      </div>
                    ))}
                  </div>
                )}
                {submission.admin_notes && (
                  <div style={{marginTop:16,background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'12px 16px'}}>
                    <div style={{fontSize:12,fontWeight:600,color:'#92400e'}}>Admin Notes</div>
                    <div style={{fontSize:13,color:'#78350f',marginTop:4}}>{submission.admin_notes}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FORM TAB */}
      {tab==='form' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">My Details & Letter Request</span>
            {!canEdit && <span className="badge badge-green">Approved</span>}
          </div>
          <div className="card-body">
            {!canEdit && <div style={{background:'#d1fae5',border:'1px solid #6ee7b7',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#065f46',marginBottom:16}}>✅ Approved. Contact admin to make changes.</div>}

            <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--slate-100)'}}>📋 Contact Details</div>
            <div className="form-grid" style={{marginBottom:20}}>
              <div className="form-group"><label>Phone</label><input disabled={!canEdit} value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+94 77 123 4567" /></div>
              <div className="form-group full"><label>Address</label><textarea disabled={!canEdit} value={form.address} onChange={e=>set('address',e.target.value)} /></div>
              <div className="form-group"><label>Emergency Contact Name</label><input disabled={!canEdit} value={form.emergency_contact_name} onChange={e=>set('emergency_contact_name',e.target.value)} /></div>
              <div className="form-group"><label>Emergency Contact Phone</label><input disabled={!canEdit} value={form.emergency_contact_phone} onChange={e=>set('emergency_contact_phone',e.target.value)} /></div>
            </div>

            <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--slate-100)'}}>🎓 University & Internship</div>
            <div className="form-grid" style={{marginBottom:20}}>
              <div className="form-group full"><label>University / Institute</label><input disabled={!canEdit} value={form.university} onChange={e=>set('university',e.target.value)} placeholder="e.g. University of Moratuwa" /></div>
              <div className="form-group"><label>Faculty</label><input disabled={!canEdit} value={form.faculty} onChange={e=>set('faculty',e.target.value)} placeholder="Faculty of Information Technology" /></div>
              <div className="form-group"><label>Degree / Programme</label><input disabled={!canEdit} value={form.degree} onChange={e=>set('degree',e.target.value)} placeholder="BSc (Hons) in IT" /></div>
              <div className="form-group"><label>Internship Start</label><input type="date" disabled={!canEdit} value={form.internship_start} onChange={e=>set('internship_start',e.target.value)} /></div>
              <div className="form-group"><label>Internship End</label><input type="date" disabled={!canEdit} value={form.internship_end} onChange={e=>set('internship_end',e.target.value)} /></div>
              <div className="form-group full"><label>Project Title</label><input disabled={!canEdit} value={form.project_title} onChange={e=>set('project_title',e.target.value)} /></div>
            </div>

            <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--slate-100)'}}>📄 Letter Request</div>
            <div className="form-grid" style={{marginBottom:20}}>
              <div className="form-group"><label>Letter Type</label>
                <select disabled={!canEdit} value={form.letter_type} onChange={e=>set('letter_type',e.target.value)}>
                  <option value="Internship">Internship Completion Letter</option>
                  <option value="Associate">Associate Software Engineer</option>
                  <option value="Service">Service / Experience Letter</option>
                  <option value="Appointment">Appointment Letter</option>
                </select>
              </div>
              {(form.letter_type==='Internship'||form.letter_type==='Associate') && (
                <div className="form-group"><label>Employment Type</label>
                  <select disabled={!canEdit} value={form.time_type} onChange={e=>set('time_type',e.target.value)}>
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                  </select>
                </div>
              )}
              <div className="form-group"><label>Requested By (optional)</label><input disabled={!canEdit} value={form.requested_by} onChange={e=>set('requested_by',e.target.value)} placeholder="e.g. University of Moratuwa" /></div>
              <div className="form-group"><label>Signatory Name</label><input disabled={!canEdit} value={form.signatory_name} onChange={e=>set('signatory_name',e.target.value)} /></div>
              <div className="form-group"><label>Signatory Title</label><input disabled={!canEdit} value={form.signatory_title} onChange={e=>set('signatory_title',e.target.value)} /></div>
            </div>

            <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:8,paddingBottom:8,borderBottom:'1px solid var(--slate-100)'}}>⚡ Duties & Responsibilities</div>
            <ResponsibilityPicker
              responsibilities={responsibilities}
              setResponsibilities={setResponsibilities}
              templates={templates}
              disabled={!canEdit}
            />
          </div>

          {canEdit && (
            <div className="modal-footer" style={{gap:10}}>
              <button className="btn btn-outline" onClick={handlePreview} disabled={previewing}>
                <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{width:14,height:14}}><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                {previewing ? 'Generating…' : 'Preview Letter'}
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Submitting…' : submission ? '🔄 Resubmit' : '📤 Submit for Approval'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* PROFILE TAB */}
      {tab==='profile' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div className="card">
            <div className="card-header"><span className="card-title">Personal Info</span></div>
            <div className="card-body">
              {[['NIC',emp.nic],['Date of Birth',emp.date_of_birth?new Date(emp.date_of_birth).toLocaleDateString('en-GB'):'—'],['Gender',emp.gender],['Nationality',emp.nationality],['Address',emp.address],['Phone',emp.phone]].map(([k,v])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--slate-100)'}}>
                  <span style={{fontSize:12,fontWeight:600,color:'var(--slate-500)'}}>{k}</span>
                  <span style={{fontSize:13,color:'var(--navy)',maxWidth:200,textAlign:'right'}}>{v||'—'}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Current Position</span></div>
            <div className="card-body">
              {current ? (
                <>
                  <div style={{fontSize:17,fontWeight:700,color:'var(--navy)'}}>{current.designation_title}</div>
                  <div style={{fontSize:13,color:'var(--slate-500)',marginTop:2}}>{current.department}</div>
                  <div style={{display:'flex',gap:8,marginTop:10,flexWrap:'wrap'}}>
                    <span className="badge badge-indigo">{current.employment_type}</span>
                    <span className="badge badge-slate">Since {new Date(current.start_date).toLocaleDateString('en-GB')}</span>
                  </div>
                </>
              ) : <div style={{color:'var(--slate-400)',fontSize:13}}>No active position.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
